
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const SubscriptionScreen: React.FC = () => {
    const { t } = useTranslation();
    const { user, refreshSubscription, marcAvatarUrl, userRole, subscriptionStatus, signOut } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState<any>(null);
    const [fetchingConfig, setFetchingConfig] = useState(true);
    const [isLatam, setIsLatam] = useState(false);
    const [activeTab, setActiveTab] = useState<'quarterly' | 'monthly'>('quarterly');

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const { data } = await supabase.from('settings').select('*').limit(1).single();
                if (data) setConfig(data);
            } catch (e) {
                console.warn('Error fetching subscription config:', e);
            } finally {
                setFetchingConfig(false);
            }
        };
        loadConfig();

        // Detect LATAM Location Silently
        const detectLocation = async () => {
            try {
                const res = await fetch('https://ipapi.co/json/');
                if (res.ok) {
                    const data = await res.json();
                    const latamCodes = ['AR', 'BO', 'BR', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE'];
                    if (latamCodes.includes(data.country_code)) {
                        setIsLatam(true);
                        console.log("[Location] LATAM region detected.");
                    }
                }
            } catch (e) {
                console.warn("Could not geolocate IP silently.", e);
            }
        };
        detectLocation();
    }, []);

    // Get Active Pricing
    const getActiveConfig = () => {
        if (!config) return null;
        return {
            ...config,
            quarterly_price: (isLatam && config.latam_quarterly_price) ? config.latam_quarterly_price : config.quarterly_price,
            quarterly_link: (isLatam && config.latam_quarterly_link) ? config.latam_quarterly_link : config.quarterly_link,
            monthly_price: (isLatam && config.latam_monthly_price) ? config.latam_monthly_price : config.monthly_price,
            monthly_link: (isLatam && config.latam_monthly_link) ? config.latam_monthly_link : config.monthly_link,
            
            // Monthly Coupon Configuration
            monthly_coupon_active: isLatam ? (config.latam_monthly_coupon_active ?? false) : (config.monthly_coupon_active ?? false),
            monthly_coupon_code: isLatam ? (config.latam_monthly_coupon_code ?? '') : (config.monthly_coupon_code ?? ''),
            monthly_coupon_discount_amount: isLatam ? (config.latam_monthly_coupon_discount_amount ?? 0) : (config.monthly_coupon_discount_amount ?? 0),
            monthly_coupon_discount_type: isLatam ? (config.latam_monthly_coupon_discount_type ?? 'fixed') : (config.monthly_coupon_discount_type ?? 'fixed'),

            // Quarterly Coupon Configuration
            quarterly_coupon_active: isLatam ? (config.latam_quarterly_coupon_active ?? false) : (config.quarterly_coupon_active ?? false),
            quarterly_coupon_code: isLatam ? (config.latam_quarterly_coupon_code ?? '') : (config.quarterly_coupon_code ?? ''),
            quarterly_coupon_discount_amount: isLatam ? (config.latam_quarterly_coupon_discount_amount ?? 0) : (config.quarterly_coupon_discount_amount ?? 0),
            quarterly_coupon_discount_type: isLatam ? (config.latam_quarterly_coupon_discount_type ?? 'fixed') : (config.quarterly_coupon_discount_type ?? 'fixed'),
        };
    };
    
    const displayConfig = getActiveConfig();

    React.useLayoutEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, []);

    // V701: AUTOMATIC SUBSCRIPTION DETECTION
    // When the user returns from the Stripe checkout (Safari/Browser -> PWA),
    // visibilitychange fires and we check if the webhook already authorized them.
    React.useEffect(() => {
        const handleFocus = () => {
            if (document.visibilityState === 'visible') {
                console.log("[Subscription] App regained focus, checking status automatically...");
                checkStatus(true); // Pass flag to hide alert if not found silently
            }
        };
        window.addEventListener('visibilitychange', handleFocus);
        return () => window.removeEventListener('visibilitychange', handleFocus);
    }, [user?.id]); // Note: Using user.id to remount if user changes

    // Also watch global AuthContext subscription string
    React.useEffect(() => {
        if (subscriptionStatus && ['active', 'trialing', 'pro'].includes(subscriptionStatus)) {
            console.log("[Subscription] Global status is PRO, verifying instantly...");
            checkStatus(false);
        }
    }, [subscriptionStatus]);

    const handleSubscribe = (linkUrl: string, planType: 'monthly' | 'quarterly') => {
        if (!linkUrl) return;
        try {
            const checkoutUrl = new URL(linkUrl);
            if (user?.id) checkoutUrl.searchParams.set('client_reference_id', user.id);
            if (user?.email) checkoutUrl.searchParams.set('prefilled_email', user.email);
            
            if (!checkoutUrl.searchParams.has('prefilled_promo_code')) {
                if (planType === 'monthly') {
                    if (displayConfig?.monthly_coupon_active && displayConfig?.monthly_coupon_code) {
                        checkoutUrl.searchParams.set('prefilled_promo_code', displayConfig.monthly_coupon_code);
                    }
                } else {
                    if (displayConfig?.quarterly_coupon_active && displayConfig?.quarterly_coupon_code) {
                        checkoutUrl.searchParams.set('prefilled_promo_code', displayConfig.quarterly_coupon_code);
                    }
                }
            }
            
            // Sync Stripe Language with Aura Method Language
            const activeLang = localStorage.getItem('aura_language') || 'es';
            checkoutUrl.searchParams.set('locale', activeLang.startsWith('en') ? 'en' : 'es');

            window.open(checkoutUrl.toString(), '_blank');
        } catch (e) {
            console.error("Invalid stripe URL:", e);
        }
    };

    const checkStatus = async (silent = false) => {
        setLoading(true);
        let foundPro = false;

        try {
            for (let i = 0; i < 4; i++) {
                await refreshSubscription();

                // Verify status directly from DB
                const { data: profile } = await supabase.from('profiles').select('subscription_status, stats').eq('id', user?.id).single();
                const status = profile?.subscription_status;

                const isPro = ['active', 'trialing', 'pro'].includes(status || '');

                if (isPro) {
                    foundPro = true;
                    console.log("[Subscription] Subscription Verified. Initializing Diet Generation anchors...");

                    const currentStats = profile?.stats || {};
                    const startTime = Date.now();

                    const hasPhotos = currentStats.photos?.front && currentStats.photos?.side && currentStats.photos?.back;
                    const hasValidPlan = currentStats.nutritionPlan && currentStats.nutritionPlan.comidas && currentStats.nutritionPlan.comidas.length > 0;
                    const isUpdate = !!hasValidPlan;
                    const targetTime = startTime + (isUpdate ? (30 * 60 * 1000) : (90 * 60 * 1000)); // 30 mins for update, 90 mins for initial

                    const nextDietStatus = !hasPhotos ? 'pending_photos' : (isUpdate ? 'trigger_update' : 'generating_initial');

                    const updatedStats = {
                        ...currentStats,
                        aura_diet_status: nextDietStatus,
                        aura_diet_process_start: hasPhotos ? startTime : null,
                        aura_target_completion_time: hasPhotos ? targetTime : null,
                        aura_generation_type: isUpdate ? 'update' : 'new',
                        premium_since: currentStats.premium_since || new Date().toISOString()
                    };

                    await supabase.from('profiles').update({
                        stats: updatedStats,
                        aura_diet_status: nextDietStatus,
                        aura_diet_process_start: hasPhotos ? startTime : null,
                        aura_target_completion_time: hasPhotos ? targetTime : null,
                        subscription_status: status // Ensure sync
                    }).eq('id', user?.id);

                    // Local sync
                    localStorage.setItem('aura_subscription_status', status || 'active');
                    localStorage.setItem('aura_diet_status', nextDietStatus);
                    if (hasPhotos) {
                        localStorage.setItem('aura_diet_process_start', startTime.toString());
                        localStorage.setItem('aura_target_completion_time', targetTime.toString());
                    } else {
                        localStorage.removeItem('aura_diet_process_start');
                        localStorage.removeItem('aura_target_completion_time');
                    }
                    localStorage.setItem('aura_generation_type', updatedStats.aura_generation_type);

                    // Trigger Worker (Forces immediate check)
                    window.dispatchEvent(new Event('aura_diet_updated'));
                    
                    // V3000: DIRECT INVOCATION
                    // If they didn't have a plan, start generation immediately, bypassing pg_net
                    if (!isUpdate && hasPhotos) {
                        supabase.functions.invoke('generate-diet', {
                            body: { user_id: user?.id },
                            headers: { 'x-manual-invoke': 'true' }
                        }).catch(console.error);
                    }

                    // Flags for Marc's welcome
                    localStorage.setItem('aura_pending_welcome_sequence', 'true');
                    localStorage.setItem('aura_force_welcome', 'true');

                    navigate('/premium-welcome');
                    return; // BREAK EARLY
                }
                
                // If not found yet, wait 2 seconds and retry (except on last iteration)
                if (i < 3) await new Promise(resolve => setTimeout(resolve, 2000));
            }

            if (!foundPro && !silent) {
                alert(t('subscription.not_detected'));
            }
        } catch (e) {
            console.error(e);
            if (!silent) {
                alert(t('subscription.connection_error'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper to calculate pricing, discounts and badge labels
    const calculatePricing = (basePrice: number, isActive: boolean, discountAmount: number, discountType: string) => {
        if (!isActive || !discountAmount || discountAmount <= 0) {
            return {
                hasDiscount: false,
                basePrice,
                finalPrice: basePrice,
                discountBadge: '',
                tabBadge: ''
            };
        }

        let finalPrice = basePrice;
        let discountBadge = '';
        let tabBadge = '';

        if (discountType === 'percentage') {
            finalPrice = Math.max(0, basePrice * (1 - discountAmount / 100));
            tabBadge = `(-${discountAmount}%)`;
            discountBadge = `OFERTA DE BIENVENIDA · AHORRAS ${discountAmount}%`;
        } else {
            finalPrice = Math.max(0, basePrice - discountAmount);
            const percent = Math.round((discountAmount / basePrice) * 100);
            tabBadge = `(-${percent}%)`;
            discountBadge = `OFERTA DE BIENVENIDA · AHORRAS ${discountAmount}${isLatam ? '$' : '€'} (-${percent}%)`;
        }

        return {
            hasDiscount: true,
            basePrice,
            finalPrice: Number(finalPrice.toFixed(2)),
            discountBadge,
            tabBadge
        };
    };

    const quarterlyPricing = calculatePricing(
        Number(displayConfig?.quarterly_price || (isLatam ? 49.90 : 89)),
        !!displayConfig?.quarterly_coupon_active,
        Number(displayConfig?.quarterly_coupon_discount_amount || 0),
        displayConfig?.quarterly_coupon_discount_type || 'fixed'
    );

    const monthlyPricing = calculatePricing(
        Number(displayConfig?.monthly_price || (isLatam ? 19.90 : 39)),
        !!displayConfig?.monthly_coupon_active,
        Number(displayConfig?.monthly_coupon_discount_amount || 0),
        displayConfig?.monthly_coupon_discount_type || 'fixed'
    );

    return (
        <div className="min-h-screen bg-background-dark px-6 py-8 flex flex-col items-center justify-between relative w-full overflow-x-hidden">
            {/* Background Ambience */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] aspect-square opacity-[0.08] blur-[120px] rounded-full bg-primary pointer-events-none"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] aspect-square opacity-[0.08] blur-[120px] rounded-full bg-orange-600 pointer-events-none"></div>

            {/* Header Logo (Top) */}
            <div className="relative z-10 pt-safe-top-medium text-center">
                <span className="text-[10px] font-black text-white/30 tracking-[0.5em] uppercase block">
                    AURA METHOD
                </span>
            </div>

            {/* Main Card with Switcher (Center) */}
            <div className="max-w-md w-full my-auto py-6 relative z-10">
                <div className="bg-[#0b0b0e]/90 backdrop-blur-2xl border border-white/10 p-6 rounded-[2rem] space-y-6 relative overflow-hidden shadow-[0_0_80px_rgba(242,127,13,0.08),_inset_0_1px_1px_rgba(255,255,255,0.05)] text-center">
                    <div>
                        <h2 className="text-xl font-black text-white uppercase tracking-wider">ACCESO COMPLETO</h2>
                        <p className="text-[10px] font-bold text-white/50 tracking-wide mt-1.5 max-w-[280px] mx-auto leading-relaxed">
                            Tu plan de entrenamiento, nutrición y chat directo con Marc Serrano.
                        </p>
                    </div>

                    {/* Lista de Beneficios Premium */}
                    <div className="text-left space-y-3.5 py-4 max-w-[280px] mx-auto border-t border-white/5 border-b border-white/5 my-2">
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-primary text-lg font-black mt-0.5 select-none drop-shadow-[0_0_5px_rgba(242,127,13,0.3)]">check_circle</span>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black text-white uppercase tracking-wider leading-none">Chat Directo 1-a-1</span>
                                <span className="text-[9px] text-white/40 leading-tight mt-1">Contacto diario e ilimitado con Marc Serrano.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-primary text-lg font-black mt-0.5 select-none drop-shadow-[0_0_5px_rgba(242,127,13,0.3)]">check_circle</span>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black text-white uppercase tracking-wider leading-none">Entrenamiento Pro</span>
                                <span className="text-[9px] text-white/40 leading-tight mt-1">Rutinas adaptadas a tu material y molestias.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-primary text-lg font-black mt-0.5 select-none drop-shadow-[0_0_5px_rgba(242,127,13,0.3)]">check_circle</span>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black text-white uppercase tracking-wider leading-none">Nutrición de Precisión</span>
                                <span className="text-[9px] text-white/40 leading-tight mt-1">Calorías y macros adaptados a tus gustos.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-primary text-lg font-black mt-0.5 select-none drop-shadow-[0_0_5px_rgba(242,127,13,0.3)]">check_circle</span>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black text-white uppercase tracking-wider leading-none">Reajustes Semanales</span>
                                <span className="text-[9px] text-white/40 leading-tight mt-1">Evolución en caliente según tu progreso real.</span>
                            </div>
                        </div>
                    </div>

                    {fetchingConfig ? (
                        <div className="w-full h-40 bg-white/5 animate-pulse rounded-2xl border border-white/10"></div>
                    ) : (
                        <>
                            {/* Segment Plan Switcher */}
                            <div className="bg-[#121217] border border-white/5 p-1 rounded-2xl flex gap-1 relative z-10">
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    type="button"
                                    onClick={() => setActiveTab('quarterly')}
                                    className={`flex-1 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer ${
                                        activeTab === 'quarterly'
                                            ? 'bg-gradient-to-r from-primary to-orange-600 text-white shadow-[0_0_20px_rgba(242,127,13,0.25)] border border-primary/20'
                                            : 'text-white/40 hover:text-white/80 hover:bg-white/[0.01] border border-transparent'
                                    }`}
                                >
                                    Trimestral {quarterlyPricing.hasDiscount && (
                                        <span className={`text-[8px] font-bold block sm:inline ${activeTab === 'quarterly' ? 'text-white/80' : 'text-primary/70'}`}>
                                            {quarterlyPricing.tabBadge}
                                        </span>
                                    )}
                                </motion.button>
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    type="button"
                                    onClick={() => setActiveTab('monthly')}
                                    className={`flex-1 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer ${
                                        activeTab === 'monthly'
                                            ? 'bg-gradient-to-r from-primary to-orange-600 text-white shadow-[0_0_20px_rgba(242,127,13,0.25)] border border-primary/20'
                                            : 'text-white/40 hover:text-white/80 hover:bg-white/[0.01] border border-transparent'
                                    }`}
                                >
                                    Mensual {monthlyPricing.hasDiscount && (
                                        <span className={`text-[8px] font-bold block sm:inline ${activeTab === 'monthly' ? 'text-white/80' : 'text-primary/70'}`}>
                                            {monthlyPricing.tabBadge}
                                        </span>
                                    )}
                                </motion.button>
                            </div>

                            {/* Dynamically Updated Pricing */}
                            {activeTab === 'quarterly' ? (
                                <div className="py-4 space-y-3">
                                    {quarterlyPricing.hasDiscount ? (
                                        <>
                                            <div className="flex items-baseline justify-center gap-2">
                                                <span className="text-xl font-bold text-white/40 line-through">
                                                    {isLatam ? '$' : ''}{quarterlyPricing.basePrice}{!isLatam ? '€' : ''}
                                                </span>
                                                <p className="text-4xl font-black text-white tracking-tight">
                                                    {isLatam ? '$' : ''}{quarterlyPricing.finalPrice}{!isLatam ? '€' : ''} <span className="text-xs font-bold text-white/50 lowercase">/ trimestre</span>
                                                </p>
                                            </div>
                                            <div className="bg-primary/10 border border-primary/20 rounded-full px-4 py-1 inline-block">
                                                <span className="text-[9px] font-black text-primary uppercase tracking-widest block animate-pulse">
                                                    {quarterlyPricing.discountBadge}
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-4xl font-black text-white tracking-tight">
                                                {isLatam ? '$' : ''}{quarterlyPricing.basePrice}{!isLatam ? '€' : ''} <span className="text-xs font-bold text-white/50 lowercase">/ trimestre</span>
                                            </p>
                                            <div className="bg-white/5 border border-white/10 rounded-full px-4 py-1 inline-block">
                                                <span className="text-[9px] font-black text-white/60 uppercase tracking-widest block">
                                                    PLAN TRIMESTRAL
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="py-4 space-y-3">
                                    {monthlyPricing.hasDiscount ? (
                                        <>
                                            <div className="flex items-baseline justify-center gap-2">
                                                <span className="text-xl font-bold text-white/40 line-through">
                                                    {isLatam ? '$' : ''}{monthlyPricing.basePrice}{!isLatam ? '€' : ''}
                                                </span>
                                                <p className="text-4xl font-black text-white tracking-tight">
                                                    {isLatam ? '$' : ''}{monthlyPricing.finalPrice}{!isLatam ? '€' : ''} <span className="text-xs font-bold text-white/50 lowercase">/ mes</span>
                                                </p>
                                            </div>
                                            <div className="bg-primary/10 border border-primary/20 rounded-full px-4 py-1 inline-block">
                                                <span className="text-[9px] font-black text-primary uppercase tracking-widest block animate-pulse">
                                                    {monthlyPricing.discountBadge}
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-4xl font-black text-white tracking-tight">
                                                {isLatam ? '$' : ''}{monthlyPricing.basePrice}{!isLatam ? '€' : ''} <span className="text-xs font-bold text-white/50 lowercase">/ mes</span>
                                            </p>
                                            <div className="bg-white/5 border border-white/10 rounded-full px-4 py-1 inline-block">
                                                <span className="text-[9px] font-black text-white/60 uppercase tracking-widest block">
                                                    PLAN FLEXIBLE SIN PERMANENCIA
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Dynamically Updated Action CTA Button */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    if (activeTab === 'quarterly') {
                                        handleSubscribe(displayConfig?.quarterly_link, 'quarterly');
                                    } else {
                                        handleSubscribe(displayConfig?.monthly_link, 'monthly');
                                    }
                                }}
                                className="w-full relative overflow-hidden py-4 bg-neutral-900 text-white font-black uppercase tracking-[0.2em] rounded-2xl transition-all shadow-[0_10px_30px_rgba(242,127,13,0.15)] hover:shadow-[0_20px_50px_rgba(242,127,13,0.35)] text-[11px] flex items-center justify-center gap-2 border border-primary/40 cursor-pointer group"
                            >
                                {/* Pulsing Aura Border */}
                                <div className="absolute inset-0 rounded-2xl border-2 border-primary/20 animate-pulse-slow pointer-events-none"></div>

                                {/* Shimmer Sweep */}
                                <motion.div
                                    animate={{
                                        left: ['-100%', '200%'],
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        repeatDelay: 2.5,
                                        ease: "easeInOut"
                                    }}
                                    className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-primary/25 to-transparent skew-x-[30deg] pointer-events-none"
                                />

                                <span className="relative z-10 bg-gradient-to-r from-white via-white to-primary/45 bg-clip-text text-transparent flex items-center justify-center gap-2">
                                    Comenzar mi cambio
                                    <span className="material-symbols-outlined text-sm font-black">arrow_forward</span>
                                </span>
                            </motion.button>
                        </>
                    )}

                    {/* Trust and Guarantee block */}
                    <div className="flex items-center justify-center gap-1.5 text-center text-[9px] font-bold text-white/30 uppercase tracking-wider pt-4 border-t border-white/5">
                        <span className="material-symbols-outlined text-xs text-primary/60 font-black">shield_with_heart</span>
                        Garantía de reembolso de 14 días · Stripe
                    </div>
                </div>
            </div>

            {/* Footer Links & Verification (Bottom) */}
            <div className="relative z-10 pb-safe flex flex-col items-center gap-3 w-full max-w-xs">
                <button
                    onClick={() => checkStatus(false)}
                    className="text-[9px] text-white/50 hover:text-white uppercase tracking-widest font-black transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                    {loading ? <span className="animate-spin material-symbols-outlined text-[10px]">sync</span> : <span className="material-symbols-outlined text-[10px]">verified</span>}
                    ¿Ya te has suscrito? Verificar compra
                </button>

                <button 
                    onClick={async () => {
                        try {
                            await signOut();
                            navigate('/login');
                        } catch (e) {
                            console.error(e);
                            navigate('/login');
                        }
                    }} 
                    className="text-[9px] text-white/30 hover:text-white/60 hover:underline cursor-pointer uppercase tracking-widest font-bold"
                >
                    Cerrar sesión
                </button>
            </div>

            {/* DEV ONLY SIMULATION BUTTON */}
            {userRole === 'admin' && localStorage.getItem('aura_dev_mode') !== 'false' && (
                <button
                    onClick={async () => {
                        if (!user) return;
                        try {
                            await supabase.from('profiles').update({ subscription_status: 'trialing' }).eq('id', user.id);
                            await checkStatus();
                        } catch (e) {
                            alert(t('subscription.error_simulating', { error: e }));
                        }
                    }}
                    className="fixed top-24 right-4 z-50 bg-red-500/20 backdrop-blur-md border border-red-500/50 text-red-500 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all shadow-lg cursor-pointer"
                >
                    {t('subscription.dev_simulate_payment')}
                </button>
            )}
        </div>
    );
};

export default SubscriptionScreen;
